import {
  Box,
  BoxProps,
  Button,
  IconButton,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
  ToggleButtonProps,
} from '@mui/material';
import React, { ReactElement } from 'react';
import { Palette } from 'Palette';
import { BUTTON_FONT_SIZE, RADIUS } from 'DesignTokens';
import { determineNextTone } from './styles';

export const StyledButtonGroup = styled(Box)<{
  justifyContent?: string;
  className?: string;
}>(({ theme, justifyContent = 'center', className }) => ({
  display: 'flex',
  flexDirection: 'row', // Default for sm+
  justifyContent,
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
  '& .MuiToggleButtonGroup-root': {
    width: { lg: '100%' },
    mx: { xs: 0 },
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    width: 'default',
  },

  className: `react-StyledButtonGroup ${className || ''}`.trim(),
}));

const BUTTON_VARIANT_STYLES = {
  default: {
    border: 'none',
    bg: Palette.default.buttonBg,
    text: Palette.default.buttonText,
    hoverBg: Palette[determineNextTone('default')].buttonBg || Palette.default.buttonBg,
    hoverText: Palette[determineNextTone('default')].buttonText,
  },
  inactiveOrCancel: {
    border: `1px solid ${Palette.default.inactiveOrCancelButtonText}`,
    bg: Palette.default.inactiveOrCancelButtonBg,
    text: Palette.default.inactiveOrCancelButtonText,
    hoverBg: Palette.default.buttonBg,
    hoverText: Palette.default.buttonText,
  },
  // For actions that are destructive but not cancel/secondary — e.g. deleting a
  // record — distinct from both the primary brand colour and the plain
  // secondary outline.
  warning: {
    border: 'none',
    bg: Palette.warning.buttonBg,
    text: Palette.warning.buttonText,
    hoverBg: Palette.warning.activeBg,
    hoverText: Palette.warning.activeText,
  },
} as const;

// inactiveOrCancel's own hover (full primary navy) reads correctly for a
// plain standalone Cancel button, but when a secondary button sits right next
// to a primary one (e.g. a row of action buttons), hovering it then makes it
// momentarily LOOK like the primary button's resting state — confusing,
// since it reads as if the two buttons swapped roles. Opt into this instead
// wherever a secondary button shares a row with a primary one: it hovers to
// the same lighter tone the primary button's own hover uses.
export const SECONDARY_BUTTON_HOVER_SX = {
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: Palette.tone1.buttonBg,
      color: Palette.tone1.buttonText,
    },
  },
};

export const StyledButton = styled(Button, {
  shouldForwardProp: (prop) =>
    !['bottomSpacing', 'buttonVariant', 'noTopSpacing', 'compact'].includes(prop as string),
})<{
  bottomSpacing?: boolean;
  className?: string;
  buttonVariant?: 'default' | 'inactiveOrCancel' | 'warning';
  noTopSpacing?: boolean;
  // Shrinks sizing for tight spaces (e.g. a kanban column) while keeping the
  // same colours/hover behaviour as a regular StyledButton.
  compact?: boolean;
}>(({
  theme,
  bottomSpacing = false,
  className,
  buttonVariant = 'default',
  noTopSpacing = false,
  compact = false,
}) => {
  const variant = BUTTON_VARIANT_STYLES[buttonVariant];

  return {
    marginTop: noTopSpacing ? 0 : theme.spacing(2),
    marginBottom: bottomSpacing ? theme.spacing(2) : 0,
    border: variant.border,
    backgroundColor: variant.bg,
    minWidth: compact ? 'auto' : '6.25rem', // 80px in rem
    height: compact ? '1.5rem' : '2.5rem',
    fontFamily: 'Inter, sans-serif',
    fontSize: compact ? '0.6875rem' : BUTTON_FONT_SIZE,
    padding: compact ? '0 0.5rem' : '0 1.25rem', // side padding 20px as em
    color: variant.text,
    ['@media (hover: none)']: {
      '&:hover': {
        backgroundColor: variant.bg,
        color: variant.text,
      },
    },
    ['@media (hover: hover)']: {
      '&:hover': {
        backgroundColor: variant.hoverBg,
        color: variant.hoverText,
      },
    },
    borderRadius: RADIUS.button,
    textTransform: 'capitalize',
    className: `react-StyledButton ${className || ''}`.trim(),
    '&.Mui-disabled': {
      backgroundColor: Palette.default.disabledBg,
      color: Palette.default.disabledText,
    },
  };
});

interface StyledButtonPanelProps extends BoxProps {
  children: ReactElement<typeof StyledButton> | ReactElement<typeof StyledButton>[];
  buttonPosition?: 'center' | 'left' | 'right';
}

export const StyledButtonPanel: React.FC<StyledButtonPanelProps> = ({
  children,
  buttonPosition = 'center',
  className,
  ...rest
}) => {
  const validChildren = React.Children.toArray(children).filter(
    (child) =>
      React.isValidElement(child) &&
      (child.type === StyledButton || (child.type as any).displayName === StyledButton.displayName),
  );

  return (
    <Box
      display="flex"
      justifyContent={
        buttonPosition === 'left'
          ? 'flex-start'
          : buttonPosition === 'right'
            ? 'flex-end'
            : 'center'
      }
      alignItems="center"
      gap={2}
      flexWrap="wrap"
      className={`react-StyledButtonPanel ${className || ''}`.trim()}
      {...rest}
    >
      {validChildren}
    </Box>
  );
};

interface StyledToggleButtonGroupProps extends ToggleButtonGroupProps {
  segmented?: boolean;
  mini?: boolean;
}

export const StyledToggleButtonGroup = styled(ToggleButtonGroup, {
  shouldForwardProp: (prop) => prop !== 'segmented' && prop !== 'mini',
})<StyledToggleButtonGroupProps>(({ segmented = true, mini = false, theme }) => {
  return {
    '& .MuiToggleButtonGroup-grouped': {
      ...(segmented
        ? {
            borderRadius: undefined,
            fontSize: mini ? '0.875rem' : '1rem',
            paddingLeft: mini ? '0.5rem' : '1.5rem',
            paddingRight: mini ? '0.5rem' : '1.5rem',
            height: mini ? '2rem' : '2.25rem',
            flex: { lg: '1 1 auto' },
            whiteSpace: 'nowrap',
            border: `1px solid ${Palette['default'].inactiveOrCancelButtonText}`,
          }
        : {
            borderRadius: RADIUS.button,
            margin: '0 0.25rem',
            border: 'none',
          }),
    },
    [theme.breakpoints.up('lg')]: segmented
      ? {
          '& .MuiToggleButtonGroup-grouped': {
            minWidth: mini ? '2rem' : '8.4375rem',
            width: '100%',
          },
        }
      : {},
    [theme.breakpoints.down('md')]: {
      justifyContent: 'flex-end',
    },
  };
});
interface StyledToggleButtonProps extends ToggleButtonProps {
  mini?: boolean;
}

export const StyledToggleButton = styled(ToggleButton, {
  shouldForwardProp: (prop) => prop !== 'mini',
})<StyledToggleButtonProps>(({ theme, selected, className, mini = false }) => {
  const base = Palette.default;
  return {
    backgroundColor: selected ? base.buttonBg : base.inactiveOrCancelButtonBg,
    color: selected ? base.buttonText : base.inactiveOrCancelButtonText,
    height: mini ? '2rem' : '2.5rem',
    padding: mini ? '0.25rem' : '0 10px',
    fontFamily: 'Inter, sans-serif',
    fontSize: mini ? '0.75rem' : BUTTON_FONT_SIZE,
    textTransform: 'capitalize',
    borderRadius: RADIUS.button,
    border: 'none',
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ['@media (hover: none)']: {
      '&:hover': {
        backgroundColor: selected ? base.buttonBg : base.inactiveOrCancelButtonBg,
        color: selected ? base.buttonText : base.inactiveOrCancelButtonText,
      },
    },
    ['@media (hover: hover)']: {
      '&:hover': {
        backgroundColor: (selected
          ? Palette[determineNextTone('default')]
          : Palette[determineNextTone('default', 2)]
        ).buttonBg,
        color: Palette[determineNextTone('default')].buttonText,
      },
    },
    '&:not(.Mui-selected)': {
      border: `1px solid ${base.inactiveOrCancelButtonText}`,
    },
    '&.Mui-selected': {
      backgroundColor: base.buttonBg,
      color: base.buttonText,

      ['@media (hover: none)']: {
        '&:hover': {
          backgroundColor: base.buttonBg,
          color: base.buttonText,
        },
      },
      ['@media (hover: hover)']: {
        '&:hover': {
          backgroundColor: Palette[determineNextTone('default')].buttonBg,
          color: Palette[determineNextTone('default')].buttonText,
        },
      },
    },
    className: `react-StyledToggleButton ${className || ''}`.trim(),
    [theme.breakpoints.up('lg')]: {
      minWidth: mini ? '2.25rem' : '11.25rem',
    },
  };
});

// StyledToggleButton's own unselected hover (shared with the Investor UI and other admin
// surfaces) lands on a dark background with dark text — readable for its other callers' colour
// combinations, but not for a segmented-control-style group (e.g. a View toggle, an import scope
// picker) read as one continuous control. Opt-in via sx, so the shared default elsewhere stays
// untouched.
export const TOGGLE_GROUP_HOVER_SX = {
  '@media (hover: hover)': {
    '&:hover:not(.Mui-selected)': {
      backgroundColor: Palette.tone1.buttonBg,
      color: Palette.tone1.buttonText,
    },
  },
};

// StyledToggleButton drops the border entirely on the selected option (it relies on the filled
// background to read as "active" for its other callers). In a segmented-control-style group that
// leaves a visible gap in the group's outline where the selected button sits — this gives the
// selected option the same border as the rest so the group reads as one continuous control.
export const TOGGLE_GROUP_SELECTED_BORDER_SX = {
  '&.Mui-selected': {
    border: `1px solid ${Palette.default.inactiveOrCancelButtonText}`,
  },
};

// Sized to comfortably fit a label as long as "Update Content"/"Whole Document" with extra side
// padding, so every option in a `mini` segmented-control-style group (the View toggle, the
// document import scope picker) reads as one consistent width. A fixed `width` (not `minWidth`)
// is required: minWidth only sets a floor, so longer labels would still render wider than shorter
// ones and the options would drift out of sync. Cancels StyledToggleButton's own `marginTop`
// (meant for stacking below other controls elsewhere), which would otherwise misalign the row.
export const TOGGLE_GROUP_WIDTH_SX = { width: '7.5rem', justifyContent: 'center', marginTop: 0 };

export const StyledIconButton = styled(IconButton)(() => ({
  // custom styles, e.g.
  borderRadius: RADIUS.button,
  color: Palette.default.buttonText,
  backgroundColor: Palette.default.buttonBg,
  '&:hover': {
    backgroundColor: Palette.tone1.buttonBg,
    color: Palette.tone1.buttonText,
  },
  padding: 6,
  // etc...
}));
